import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Tooltip, Tooltip as MuiTooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { allowedServices, getRestrictedServiceTooltipMessage, isDiscontinuedService, isCMPAdmin } from 'utils';
import { canAccessCatalog } from 'utils/announcementRestrictions';
import { Day2CatalogDefinitionId, ServiceAccountDefinitionId, ProjectOnboardCatalogDefinitionId } from 'utils/catalogDefinitionId';
import { COMING_SOON_MESSAGE, START_PROVISIONING_MESSAGE } from 'utils/constant';
import { getDiscontinuedButtonLabel, getDiscontinuedDescription } from 'utils/discontinuedCatalogs';
import Recommendation from './style';

const RecommendationCard = ({
    forceToShowLearnMore,
    catalogServiceCode,
    isRICatalog,
    iconSrc,
    cmpUri,
    title,
    description,
    tags,
    primaryButtonText,
    secondaryButtonText,
    data,
    onClickLearnMoreBtn,
    selectedTabCatalogType,
}) => {
    const maxVisibleTags = 3;
    const history = useHistory();

    const { visibleTags, remainingTags } = useMemo(
        () => ({
            visibleTags: tags.slice(0, maxVisibleTags),
            remainingTags: tags.slice(maxVisibleTags),
        }),
        [tags]
    );

    const tooltipProps = () => ({
        tooltip: { sx: { backgroundColor: '#272727', fontSize: '10px' } },
        arrow: { sx: { color: '#272727' } },
    });

    const secondaryButtonStyles = useMemo(
        () => ({
            ...Recommendation.Card.buttons.secondary,
            ...(((isRICatalog === false && !forceToShowLearnMore) || selectedTabCatalogType === 'SERVICES') && { display: 'none' }),
            ...((isRICatalog === true || forceToShowLearnMore) && { visibility: isRICatalog ? 'visible' : 'hidden' }),
        }),
        [isRICatalog, forceToShowLearnMore, selectedTabCatalogType]
    );

    const handleStartProvisioning = useCallback(() => {
        if (canAccessCatalog(catalogServiceCode)) {
            if (catalogServiceCode.startsWith('DAY2')) {
                const url = [...Day2CatalogDefinitionId, ...ServiceAccountDefinitionId, ...ProjectOnboardCatalogDefinitionId].includes(catalogServiceCode)
                    ? `${cmpUri}/${catalogServiceCode}`
                    : `${cmpUri}`;
                history.push(url);
            } else {
                history.push(`${cmpUri}/${catalogServiceCode}`);
            }
        }
    }, [catalogServiceCode, cmpUri, history]);

    const getButtonLabel = useCallback(() => {
        if (isDiscontinuedService(catalogServiceCode) && !isCMPAdmin()) {
            return getDiscontinuedButtonLabel(catalogServiceCode);
        }
        if (allowedServices(catalogServiceCode)) {
            return primaryButtonText || START_PROVISIONING_MESSAGE;
        }
        return COMING_SOON_MESSAGE;
    }, [catalogServiceCode, primaryButtonText]);

    const handleLearnMoreClick = useCallback(() => {
        onClickLearnMoreBtn(data);
    }, [onClickLearnMoreBtn, data]);

    const imageElement = useMemo(() => {
        if (catalogServiceCode.startsWith('DAY2')) return null;
        return <Box component="img" src={iconSrc} alt={`${title} icon`} sx={Recommendation.Card.img} />;
    }, [catalogServiceCode, iconSrc, title]);

    const cardContent = (
        <Box sx={Recommendation.Card.container}>
            <Box sx={Recommendation.Card.subContainer}>
                <Box sx={Recommendation.Card.header}>
                    {imageElement}
                    <Tooltip title={title} arrow componentsProps={tooltipProps}>
                        <Box sx={Recommendation.Card.title}>{title}</Box>
                    </Tooltip>
                </Box>
                {isDiscontinuedService(catalogServiceCode) && <Box sx={{ mb: '14px' }}>{getDiscontinuedDescription(catalogServiceCode)}</Box>}
                {!isDiscontinuedService(catalogServiceCode) && <Box sx={Recommendation.Card.description}>{description}</Box>}
                {!isDiscontinuedService(catalogServiceCode) && (
                    <Box sx={Recommendation.Card.tags.container}>
                        {visibleTags.map((tag, index) => (
                            <Box key={index} sx={Recommendation.Card.tags.tag}>
                                {tag}
                            </Box>
                        ))}
                        {remainingTags.length > 0 && (
                            <Tooltip
                                title={
                                    <Box>
                                        {remainingTags.map((tag, index) => (
                                            <Box key={index}>{tag}</Box>
                                        ))}
                                    </Box>
                                }
                                arrow
                                componentsProps={tooltipProps}
                            >
                                <Box sx={Recommendation.Card.tags.tag}>+{remainingTags.length}</Box>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </Box>
            <Box sx={Recommendation.Card.buttons.container}>
                <Button
                    className="btn-disabled custom-btn"
                    variant={canAccessCatalog(catalogServiceCode) ? 'contained' : 'outlined'}
                    sx={Recommendation.Card.buttons.primary}
                    disableRipple
                    disableFocusRipple
                    onClick={handleStartProvisioning}
                    disabled={!canAccessCatalog(catalogServiceCode)}
                >
                    {getButtonLabel()}
                </Button>
                <Button variant="text" sx={secondaryButtonStyles} disableRipple disableFocusRipple onClick={handleLearnMoreClick}>
                    {secondaryButtonText}
                </Button>
            </Box>
        </Box>
    );

    // If service is discontinued, wrap the entire card with a discontinued tooltip
    if (isDiscontinuedService(catalogServiceCode)) {
        return (
            <MuiTooltip
                disableHoverListener
                title={getDiscontinuedDescription(catalogServiceCode)}
                arrow
                placement="top"
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#272727',
                            fontSize: '12px',
                            maxWidth: '300px',
                        },
                    },
                    arrow: { sx: { color: '#272727' } },
                }}
            >
                {cardContent}
            </MuiTooltip>
        );
    }

    // If service is not allowed, wrap the entire card with a tooltip
    if (!allowedServices(catalogServiceCode)) {
        return (
            <MuiTooltip
                title={getRestrictedServiceTooltipMessage()}
                arrow
                placement="top"
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#272727',
                            fontSize: '12px',
                            maxWidth: '300px',
                        },
                    },
                    arrow: { sx: { color: '#272727' } },
                }}
            >
                {cardContent}
            </MuiTooltip>
        );
    }

    return cardContent;
};

RecommendationCard.propTypes = {
    forceToShowLearnMore: PropTypes.any.isRequired,
    catalogServiceCode: PropTypes.string.isRequired,
    isRICatalog: PropTypes.any.isRequired,
    iconSrc: PropTypes.string.isRequired,
    cmpUri: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    primaryButtonText: PropTypes.string.isRequired,
    secondaryButtonText: PropTypes.string.isRequired,
    data: PropTypes.any.isRequired,
    onClickLearnMoreBtn: PropTypes.func.isRequired,
    selectedTabCatalogType: PropTypes.any.isRequired,
};

export default React.memo(RecommendationCard);
